import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Star, Navigation, Trash2, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { favoritesApi } from '../services/api';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { darkMode } = useTheme();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      try {
        const data = await favoritesApi.getAll();
        setFavorites(data);
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [isAuthenticated]);

  const removeFavorite = async (placeId) => {
    try {
      await favoritesApi.toggle(placeId);
      setFavorites(prev => prev.filter(p => p.id !== placeId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center p-4`}>
        <div className="text-center">
          <div className={`w-20 h-20 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Heart className={`w-10 h-10 ${textSecondary}`} />
          </div>
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Favorilerinizi Görün</h2>
          <p className={`${textSecondary} mb-6`}>Favori mekanlarınızı görmek için giriş yapın</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#2a4a73] transition-colors"
          >
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center`}>
        <div className="animate-spin w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className={`text-2xl font-bold ${textPrimary} mb-2`}>Favorilerim</h1>
        <p className={`${textSecondary} mb-6`}>
          {favorites.length > 0 
            ? `${favorites.length} mekan kaydedildi`
            : 'Henüz favori mekanınız yok'
          }
        </p>

        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <div className={`w-20 h-20 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Heart className={`w-10 h-10 ${textSecondary}`} />
            </div>
            <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Henüz favori yok</h3>
            <p className={`${textSecondary} mb-4`}>Beğendiğiniz mekanları kalp ikonuna tıklayarak kaydedin</p>
            <button
              onClick={() => navigate('/explore')}
              className="px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#2a4a73] transition-colors"
            >
              Mekanları Keşfet
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((place) => (
              <div
                key={place.id}
                className={`${bgCard} rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className="flex">
                  <img
                    src={place.image}
                    alt={place.name}
                    className="w-28 h-28 object-cover"
                  />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className={`font-semibold ${textPrimary} mb-1`}>{place.name}</h3>
                        <span className={`text-xs ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} px-2 py-1 rounded-full ${textSecondary}`}>
                          {place.category_name}
                        </span>
                      </div>
                      <button
                        onClick={() => removeFavorite(place.id)}
                        className={`p-2 text-red-500 ${darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'} rounded-full transition-colors`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className={`flex items-center gap-4 mt-3 text-sm ${textSecondary}`}>
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        {place.rating}
                      </span>
                      <span className="flex items-center gap-1">
                        <Navigation className="w-4 h-4" />
                        {place.distance}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
